import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { Button } from "@/components/button";
import { Fieldset, Legend } from "@/components/fieldset";
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/20/solid";
import useQueryGetUser from "@/apis/users/getUser";
import useDeleteUser from "@/apis/users/deleteUser";
import { useQueryGetExamReports } from "@/apis/exam_reports/getExamReports";
import { getPaginationInfo } from "@/types/pagination";
import { PaginationComponent } from "@/components/PaginationComponent";
import StudentDetails from "./components/StudentDetails";
import UserExamResultsTable from "./components/UserExamResultsTable";

const UserDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [examResultsPage, setExamResultsPage] = useState(1);

  if (!id) {
    navigate("/users");
    return null;
  }

  // Query user data
  const { data: user, isLoading, error } = useQueryGetUser(id);

  // Query exam results for this user
  const { data: examResultsData, isLoading: examResultsLoading } =
    useQueryGetExamReports({
      user_id: id,
      page: examResultsPage,
      limit: 5,
      sort: "exam_started_at",
      sort_order: "desc",
    });

  // Delete user mutation
  const { mutate: deleteUser, isPending: isDeleting } = useDeleteUser();

  // Go back to list
  const handleBack = () => {
    navigate("/users");
  };

  // Go to edit page
  const handleEdit = () => {
    navigate(`/users/${id}/edit`);
  };

  // Handle delete
  const handleDelete = () => {
    if (!user || isDeleting) return;

    if (window.confirm(`Are you sure you want to delete ${user.name}?`)) {
      setDeleteError(null);

      deleteUser(id, {
        onSuccess: () => {
          // Show success message if needed
          // Then navigate to the user list
          navigate("/users");
        },
        onError: (error: any) => {
          // Show error message
          setDeleteError(error?.message || "Failed to delete user");
        },
      });
    }
  };

  // Handle exam results pagination
  const handleExamResultsPageChange = (page: number) => {
    setExamResultsPage(page);
  };

  // Get pagination info for exam results
  const examResultsPaginationInfo = examResultsData
    ? getPaginationInfo(examResultsData)
    : null;

  // Loading state
  if (isLoading) {
    return (
      <PageContainer>
        <div className="p-8 text-center">
          <Text>Loading student information...</Text>
        </div>
      </PageContainer>
    );
  }

  // Error state
  if (error || !user) {
    return (
      <PageContainer>
        <div className="p-8 text-center text-red-600">
          <Text>Failed to load student information. Please try again.</Text>
          <Button onClick={handleBack} className="mt-4">
            Back to List
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        {deleteError && (
          <div className="p-4 bg-red-50 text-red-600 rounded-md">
            {deleteError}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button plain onClick={handleBack}>
              <ArrowLeftIcon className="h-5 w-5" />
              Back
            </Button>

            <div>
              <Heading>Student Details: {user.name}</Heading>
              <Text className="text-zinc-500">
                View complete information about this student.
              </Text>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleEdit}>
              <PencilIcon className="h-4 w-4" />
              Edit
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              <TrashIcon className="h-4 w-4" />
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>

        {/* Student Details Component */}
        <div className="p-8 border border-gray-200 rounded-md bg-white">
          <StudentDetails user={user} />
        </div>

        {/* Exam Results Section */}
        <div className="p-8 border border-gray-200 rounded-md bg-white">
          <Fieldset>
            <Legend>Exam Results</Legend>
            <div className="mt-4">
              <UserExamResultsTable
                examResults={examResultsData?.items || []}
                isLoading={examResultsLoading}
                error={null}
              />

              {/* Pagination for Exam Results */}
              {examResultsPaginationInfo &&
                examResultsPaginationInfo.totalPages > 1 && (
                  <div className="mt-6 flex justify-center">
                    <PaginationComponent
                      currentPage={examResultsPaginationInfo.currentPage}
                      totalPages={examResultsPaginationInfo.totalPages}
                      hasNextPage={examResultsPaginationInfo.hasNextPage}
                      hasPrevPage={examResultsPaginationInfo.hasPrevPage}
                      onPageChange={handleExamResultsPageChange}
                    />
                  </div>
                )}
            </div>
          </Fieldset>
        </div>
      </div>
    </PageContainer>
  );
};

export default UserDetailPage;
